// src/views/bots/BotForm.tsx
// 🎬 IMPROVED: Template as Question Type + Simplified UX
// ✅ NEW FEATURES: 
// - Template questions in question flow
// - Compact stats bar
// - Better preview positioning  
// - No separate template section
// - No response type confusion

import React, { useEffect, useMemo, useCallback, useState } from 'react';
import Header from '../../components/header';
import { Container } from 'reactstrap';
import styles from './BotForm.module.css';
import logger from '../../utils/logger';

// 🧠 LOGIC HOOKS
import { useBotForm } from './hooks/useBotForm';
import { useTemplateManagement } from './hooks/useTemplateManagement';
import { useKeywordManagement } from './hooks/useKeywordManagement';
import { useQuestionnaireManagement } from './hooks/useQuestionnaireManagement';

// 🎨 UI COMPONENTS
import BotFormHeader from './components/BotFormHeader';
import BasicInfoSection from './components/BasicInfoSection';
import KeywordSection from './components/KeywordSection';
import ResponseTypeSection from './components/ResponseTypeSection';
import QuestionnaireSection from './components/QuestionnaireSection';
// import TemplateSection from './components/TemplateSection'; // REMOVED: Templates now handled via questions
import BotAutomationSection from './components/BotAutomationSection';
import FormActions from './components/FormActions';

// 🎯 AUTOMATION DATA
import { useSelector } from 'react-redux';
import DrawerComponent from '../automation/actionsDrawer';
import ActionCommonDrawer from '../automation/actionCommonDrawer';
import ActionCreateTask from '../automation/actionCreateTask';
import ActionWebhook from '../automation/actionWebhook';
import UnifiedTemplateSelector from '../createWhatsappBroadcast/UnifiedTemplateSelector';

// 🎬 BOT PREVIEW INTEGRATION
import BotPreviewButton from './components/BotPreviewButton';

// 🎯 UPDATED INTERFACES - Enhanced with new props
interface BotFormProps {
  isEdit?: boolean;
  botId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  hideHeader?: boolean;
  onFormStateChange?: (isValid: boolean, isSaving: boolean) => void;
  
  // 🔥 EXISTING PROPS: Section control
  showOnlyBasicSections?: boolean;
  showOnlyContentSections?: boolean;
  
  // 🎯 NEW PROPS: Enhanced control
  hideResponseType?: boolean;        // Hide response type section
  showOnlyQuestionsSection?: boolean; // Only show questions (new mode)
  enableTemplateQuestions?: boolean;  // Enable template as question type
}

/**
 * 🤖 IMPROVED BOT FORM COMPONENT
 * Now supports template as question type with simplified UX
 */
const BotForm: React.FC<BotFormProps> = ({ 
  isEdit = false, 
  botId, 
  onSuccess, 
  onCancel,
  hideHeader = false,
  onFormStateChange,
  
  // Section control props
  showOnlyBasicSections = false,
  showOnlyContentSections = false,
  hideResponseType = false,
  showOnlyQuestionsSection = false,
  enableTemplateQuestions = false
}) => {
  logger.log('🤖 BotForm: Rendered - IMPROVED TEMPLATE-AS-QUESTION VERSION:', { 
    isEdit, 
    botId, 
    hideHeader,
    showOnlyBasicSections,
    showOnlyContentSections,
    hideResponseType,
    showOnlyQuestionsSection,
    enableTemplateQuestions
  });

  // 🧠 MAIN FORM LOGIC
  const formLogic = useBotForm({
    isEdit,
    botId,
    onSuccess,
    onFormStateChange
  });

  logger.log('🧠 BotForm: Form logic initialized:', {
    formDataExists: !!formLogic.formData,
    questionsCount: formLogic.formData.questions?.length || 0,
    loading: formLogic.loading,
    saving: formLogic.saving
  });

  // 🧠 TEMPLATE LOGIC
  const templateLogic = useTemplateManagement({
    selectedTemplate: formLogic.formData.selectedTemplate,
    onTemplateChange: (template) => {
      logger.log('📄 BotForm: Template changed:', template?.name || 'null');
      formLogic.updateFormData('selectedTemplate', template);
    }
  });

  // 🧠 KEYWORD LOGIC
  const keywordLogic = useKeywordManagement({
    keywords: formLogic.formData.keywords || [],
    onKeywordsChange: (keywords) => {
      logger.log('🔑 BotForm: Keywords changed:', keywords.length, 'keywords');
      formLogic.updateFormData('keywords', keywords);
    }
  });

  // 🧠 QUESTIONNAIRE LOGIC
  const questionnaireLogic = useQuestionnaireManagement({
    questions: formLogic.formData.questions || [],
    onQuestionsChange: (questions) => {
      logger.log('📋 BotForm: Questions changed:', questions.length, 'questions');
      formLogic.updateFormData('questions', questions);
    }
  });

  // 🎯 AUTOMATION DATA from store (same pattern as automation system)
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const [automationData, setAutomationData] = useState<{
    statusData: any[];
    userListData: any[];
    labelData: any[];
    leadListData: any[];
    taskData: any[];
    whatsappTemplates: any[];
    emailTemplates: any[];
  }>({
    statusData: [],
    userListData: [],
    labelData: [],
    leadListData: [],
    taskData: [],
    whatsappTemplates: [],
    emailTemplates: []
  });
  const [actionBaseDrawer, setActionBaseDrawer] = useState(false);
  const [actionDrawerTitle, setActionDrawerTitle] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [currentEditingAction, setCurrentEditingAction] = useState<{
    dayKey: string;
    actionIndex: number;
  } | null>(null);

  // 🎯 DRAWER STATE (same as automation system)
  const [delayValue, setDelayValue] = useState<number>(1);
  const [actionDaysData, setActionDaysData] = useState<any>([]);
  const [updateActionDays, setUpdateActionDays] = useState<any>("");
  const [updateTaskActionData, setUpdateTaskActionData] = useState<any>({});
  const [isActionError, setIsActionError] = useState<boolean>(false);
  const [delayMinMaxMsg, setDelayMinMaxMsg] = useState<string>("");

  // 🎯 TEMPLATE SELECTION STATE (same as automation system)
  const [isTemplateSelectOpen, setIsTemplateSelectOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  // 🎯 LOAD AUTOMATION DATA from store (same as automation system)
  useEffect(() => {
    if (!StoreData?.user?.userPreferences) return;

    const organizationUsers = StoreData?.userData?.userDetails?.organizationUsers || [];
    const organizationLeadList = StoreData?.userData?.userDetails?.organizationLeadList || [];

    setAutomationData({
      statusData: StoreData.user.userPreferences.status || [],
      userListData: organizationUsers,
      labelData: StoreData.user.userPreferences.labels || [],
      leadListData: organizationLeadList,
      taskData: StoreData.user.userPreferences.taskType || [],
      whatsappTemplates: [], // Will load from API
      emailTemplates: [] // Will load from API
    });
  }, [StoreData?.user?.userPreferences, StoreData?.userData?.userDetails, templateLogic.templateCache]);

  // 🎯 SHOW INFO ACTION (same logic as automation system)
  const ShowInfoAction = (value: string, dayKey: string) => {
    logger.log("ShowInfoAction called with:", value, dayKey);

    const dayData = formLogic.formData.dayActions[dayKey];
    const actionIndex = dayData?.findIndex((a) => a.type === value) || 0;
    const actionData = dayData?.find((a) => a.type === value);

    setCurrentEditingAction({ dayKey, actionIndex });
    setSelectedAction(value);

    // Handle email and whatsapp templates differently - go directly to template selector
    if (value === "send_whatsapp" ||
        value === "send_email" ||
        value === "send_gmail" ||
        value === "send_zeptomail") {

      // Set appropriate title for display purposes
      const actionTitle = value === "send_email" ? "Send Email" :
                          value === "send_gmail" ? "Send Gmail" :
                          value === "send_zeptomail" ? "Send ZeptoMail" :
                          "Send Whatsapp";
      setActionDrawerTitle(actionTitle);

      // If there's already a selected template, set it
      if (actionData?.data) {
        if (value === "send_whatsapp" && actionData?.data?.template) {
          setSelectedTemplate({ _id: actionData?.data?.template ?? "" });
        }
        if ((value === "send_email" || value === "send_gmail" || value === "send_zeptomail") && actionData?.data?.content) {
          setSelectedTemplate({ _id: actionData?.data?.content ?? "" });
        }
      } else {
        // Reset selected template if no data
        setSelectedTemplate(null);
      }

      // Close action drawer and open template selector directly
      setActionBaseDrawer(false);
      setTimeout(() => {
        setIsTemplateSelectOpen(true);
      }, 0);

    } else {
      // For non-template actions, open the normal drawer
      const titleMap: { [key: string]: string } = {
        'create_task': 'Create task',
        'status_change': 'Change status',
        'label_change': 'Change Label',
        'assign_lead': 'Assign lead',
        'push_notification': 'Send notification',
        'move_to_list': 'Move to list',
        'copy_to_list': 'Copy to list',
        'add_to_google_sheet': 'Add to Google Sheet',
        'add_facebook_audience': 'Add to Facebook Audience',
        'webhook': 'Send Webhook'
      };

      setActionDrawerTitle(titleMap[value] || value.replace(/_/g, ' '));
      setActionBaseDrawer(true);
    }
  };

  // 🎯 DRAWER FUNCTIONS (same as automation system)
  const handlePlusMinusValue = (e: React.ChangeEvent<HTMLInputElement>, tab: string) => {
    const { value } = e.currentTarget;
    let tempArray: any = [...actionDaysData];
    let finalNo = tab === "plus" ? delayValue + 1 : tab === "minus" ? delayValue - 1 : tab === "" ? Number(value) : 1;

    let objIndex = tempArray.findIndex((x: any) => x.type === "delay");
    if (objIndex === -1) {
      let tempObj = { type: "delay", data: { delay: finalNo } };
      tempArray.push(tempObj);
    } else {
      tempArray[objIndex].data.delay = finalNo;
    }
    setActionDaysData(tempArray);

    if (finalNo >= 1 && finalNo <= 365) {
      setDelayValue(finalNo);
      setDelayMinMaxMsg("");
    } else if (finalNo < 1) {
      setDelayMinMaxMsg("Minimum value is 1.");
    } else {
      setDelayMinMaxMsg("Maximum value is 365.");
    }
  };

  const SubActionSelectOnchange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.currentTarget;
    let selectName = selectedAction === "status_change" ? "status" : selectedAction === "label_change" ? "label" : selectedAction === "move_to_list" || selectedAction === "copy_to_list" ? "targetListId" : selectedAction === "push_notification" ? "push_notification" : selectedAction === "assign_lead" ? "assignToUser" : selectedAction;

    let tempArray: any = [...actionDaysData];
    let tempObj;
    if (selectName === "label") {
      tempObj = { type: name, data: { [selectName]: [value] } };
    } else {
      tempObj = { type: name, data: { [selectName]: value } };
    }
    setIsActionError(false);
    tempArray.push(tempObj);
    setUpdateActionDays(value);
    setActionDaysData(tempArray);
  };

  const onSaveDrawerClose = (objData: any) => {
    if (!objData || Object.keys(objData).length === 0) {
      setIsActionError(true);
      return;
    }

    if (currentEditingAction) {
      const { dayKey, actionIndex } = currentEditingAction;
      const newActions = { ...formLogic.formData.dayActions };
      if (newActions[dayKey] && newActions[dayKey][actionIndex]) {
        newActions[dayKey][actionIndex] = {
          ...newActions[dayKey][actionIndex],
          data: objData
        };
        formLogic.updateFormData('dayActions', newActions);
      }
    }

    setActionBaseDrawer(false);
    setIsActionError(false);
    setCurrentEditingAction(null);
  };

  // 🎯 TEMPLATE SELECTION HANDLER (same as automation system)
  const handleTemplateSelectFromUnified = (template: any) => {
    setSelectedTemplate(template);
    logger.log("Template selected:", template);

    if (currentEditingAction) {
      const { dayKey, actionIndex } = currentEditingAction;
      const isEmailAction = selectedAction === "send_email" ||
                             selectedAction === "send_gmail" ||
                             selectedAction === "send_zeptomail";

      const newActions = { ...formLogic.formData.dayActions };
      if (newActions[dayKey] && newActions[dayKey][actionIndex]) {
        newActions[dayKey][actionIndex] = {
          ...newActions[dayKey][actionIndex],
          data: isEmailAction
            ? { content: template._id }
            : { template: template._id }
        };
        formLogic.updateFormData('dayActions', newActions);
      }

      // Close template selector and reset editing state
      setIsTemplateSelectOpen(false);
      setCurrentEditingAction(null);
    }
  };

  logger.log('🧠 BotForm: All hooks initialized:', {
    formLogic: !!formLogic,
    templateLogic: !!templateLogic,
    keywordLogic: !!keywordLogic,
    questionnaireLogic: !!questionnaireLogic,
    questionsFromHook: questionnaireLogic.questions?.length || 0
  });

  // 🔄 Form submit handler - Single point of submission to prevent duplicates
  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    logger.log('🔥 BotForm: Form submit handler called - preventing duplicates');
    
    // Debounce rapid submissions
    if (formLogic.saving) {
      logger.log('⏳ BotForm: Already saving, ignoring duplicate submit');
      return;
    }
    
    formLogic.handleSubmit();
  }, [formLogic]);

  // 🎨 SECTION PROPS
  const basicInfoProps = useMemo(() => {
    const props = {
      name: formLogic.formData.name || '',
      description: formLogic.formData.description || '',
      isActive: formLogic.formData.isActive !== false,
      onNameChange: (name: string) => {
        logger.log('📝 BotForm: Name changed:', name);
        formLogic.updateFormData('name', name);
      },
      onDescriptionChange: (description: string) => {
        logger.log('📝 BotForm: Description changed:', description.length, 'chars');
        formLogic.updateFormData('description', description);
      },
      onActiveChange: (isActive: boolean) => {
        logger.log('📝 BotForm: Active status changed:', isActive);
        formLogic.updateFormData('isActive', isActive);
      }
    };
    logger.log('🎨 BotForm: Basic info props created:', props);
    return props;
  }, [formLogic.formData, formLogic.updateFormData]);

  const responseTypeProps = useMemo(() => {
    const props = {
      responseType: formLogic.formData.responseType || 'questionnaire', // 🔥 DEFAULT: Always questionnaire now
      onResponseTypeChange: (responseType: 'template' | 'questionnaire') => {
        logger.log('🎯 BotForm: Response type changed:', responseType);
        formLogic.updateFormData('responseType', responseType);
      }
    };
    logger.log('🎨 BotForm: Response type props created:', props);
    return props;
  }, [formLogic.formData.responseType, formLogic.updateFormData]);

  const formActionsProps = useMemo(() => {
    const props = {
      isEdit,
      saving: formLogic.saving,
      templateLoading: templateLogic.templateLoading,
      onCancel
      // Note: onSubmit removed - form submission handled by form's onSubmit
    };
    logger.log('🎨 BotForm: Form actions props created:', props);
    return props;
  }, [isEdit, formLogic.saving, templateLogic.templateLoading, onCancel]);

  // 🎬 PREVIEW DATA
  const previewData = useMemo(() => {
    const data = {
      botName: formLogic.formData.name || 'Your Bot',
      keywords: keywordLogic.keywords.length > 0 ? keywordLogic.keywords : ['hello', 'start'],
      questions: questionnaireLogic.questions || [],
      selectedTemplate: templateLogic.selectedTemplate,
      responseType: 'questionnaire' as const // 🔥 ALWAYS: questionnaire mode now
    };
    logger.log('🎬 BotForm: Preview data prepared:', {
      botName: data.botName,
      keywordsCount: data.keywords.length,
      questionsCount: data.questions.length,
      hasTemplate: !!data.selectedTemplate,
      responseType: data.responseType
    });
    return data;
  }, [
    formLogic.formData.name,
    keywordLogic.keywords,
    questionnaireLogic.questions,
    templateLogic.selectedTemplate
  ]);

  // 🎯 SECTION VISIBILITY LOGIC
  const shouldShowBasicSections = !showOnlyContentSections && !showOnlyQuestionsSection;
  const shouldShowContentSections = !showOnlyBasicSections && !showOnlyQuestionsSection;
  const shouldShowQuestionsOnly = showOnlyQuestionsSection;

  logger.log('🎯 BotForm: Section visibility:', {
    showOnlyBasicSections,
    showOnlyContentSections,
    showOnlyQuestionsSection,
    shouldShowBasicSections,
    shouldShowContentSections,
    shouldShowQuestionsOnly
  });

  // 🎨 FORM CONTENT with improved logic
  const formContent = useMemo(() => {
    logger.log('🎨 BotForm: Creating form content with improved section logic');
    
    return (
      <div className={styles.botFormContainer}>
        <form id="botForm" onSubmit={handleFormSubmit} className={styles.botForm}>
          
          {/* 🎯 FORM HEADER */}
          {!hideHeader && (
            <div className={styles.formHeaderContainer}>
              <BotFormHeader 
                isEdit={isEdit}
                onCancel={onCancel}
                saving={formLogic.saving}
                templateLoading={templateLogic.templateLoading}
              />
              {/* 🎬 BOT PREVIEW BUTTON - TOP RIGHT */}
              <div className={styles.headerPreviewButton}>
                <BotPreviewButton
                  botName={previewData.botName}
                  keywords={previewData.keywords}
                  questions={previewData.questions}
                  selectedTemplate={previewData.selectedTemplate}
                  responseType={previewData.responseType}
                  variant="primary"
                  size="large"
                  className={styles.topRightPreview}
                  onPreviewOpen={() => {
                    logger.log('🎬 BotForm: Preview opened - Form data:', {
                      botName: previewData.botName,
                      keywordsCount: previewData.keywords.length,
                      questionsCount: previewData.questions.length,
                      responseType: previewData.responseType,
                      hasTemplate: !!previewData.selectedTemplate
                    });
                  }}
                  onPreviewClose={() => {
                    logger.log('🎬 BotForm: Preview closed');
                  }}
                />
              </div>
            </div>
          )}

          <div className={styles.formContentWrapper}>
            <div className={styles.formGrid}>
            
            {/* 🎨 BASIC SECTIONS - HORIZONTAL LAYOUT */}
            {shouldShowBasicSections && (
              <div className={styles.basicSectionsContainer}>
                
                {/* Basic Information and Keywords - Side by Side */}
                <div className={styles.basicSectionsRow}>
                  <div className={styles.basicSectionLeft}>
                    <BasicInfoSection {...basicInfoProps} />
                  </div>
                  <div className={styles.basicSectionRight}>
                    <KeywordSection {...keywordLogic} />
                  </div>
                </div>

                {/* Response Type Section - Full Width Below if needed */}
                {!hideResponseType && (
                  <div className={styles.responseTypeFullWidth}>
                    <ResponseTypeSection {...responseTypeProps} />
                  </div>
                )}

              </div>
            )}

            {/* 🎨 CONTENT SECTIONS */}
            {shouldShowContentSections && (
              <div className={styles.rightColumn}>
                
                {/* 🔥 ALWAYS SHOW: Questions (since template is now a question type) */}
                <QuestionnaireSection
                  questions={questionnaireLogic.questions || []}
                  addQuestion={questionnaireLogic.addQuestion}
                  updateQuestion={questionnaireLogic.updateQuestion}
                  removeQuestion={questionnaireLogic.removeQuestion}
                  moveQuestion={questionnaireLogic.moveQuestion}
                  canMoveUp={questionnaireLogic.canMoveUp}
                  canMoveDown={questionnaireLogic.canMoveDown}
                  questionStats={questionnaireLogic.questionStats}
                  errors={{}}
                  enableTemplateQuestions={enableTemplateQuestions} // 🔥 NEW: Enable template questions
                  compactStats={true} // 🔥 NEW: Use compact stats bar
                />

                {/* 🔥 NEW: Bot Automation Section */}
                <BotAutomationSection
                  isUpdateAuto={isEdit}
                  taskData={automationData.taskData}
                  dayActions={formLogic.formData.dayActions}
                  setDayActions={(actions: any) => formLogic.updateFormData('dayActions', actions)}
                  ShowInfoAction={ShowInfoAction}
                  setDayDelays={(delays: any) => formLogic.updateFormData('dayDelays', delays)}
                  statusData={automationData.statusData}
                  userListData={automationData.userListData}
                  labelData={automationData.labelData}
                  leadListData={automationData.leadListData}
                  whatsappTemplate={automationData.whatsappTemplates}
                  emailTemplate={automationData.emailTemplates}
                  dayErrors={{}}
                  dayDelays={formLogic.formData.dayDelays}
                  dayMetrics={{}}
                />

                {/* 🔥 REMOVED: Template Section - Templates now handled via questions only */}
                {/* Templates are now integrated as question types when enableTemplateQuestions is true */}

                {/* 🎬 BOT PREVIEW SECTION - REMOVED: Now in header */}

              </div>
            )}

            {/* 🎯 QUESTIONS-ONLY MODE */}
            {shouldShowQuestionsOnly && (
              <div className={styles.singleColumn}>
                
                {/* 🔥 ENHANCED: Questions Section with Template Questions */}
                <QuestionnaireSection
                  questions={questionnaireLogic.questions || []}
                  addQuestion={questionnaireLogic.addQuestion}
                  updateQuestion={questionnaireLogic.updateQuestion}
                  removeQuestion={questionnaireLogic.removeQuestion}
                  moveQuestion={questionnaireLogic.moveQuestion}
                  canMoveUp={questionnaireLogic.canMoveUp}
                  canMoveDown={questionnaireLogic.canMoveDown}
                  questionStats={questionnaireLogic.questionStats}
                  errors={{}}
                  enableTemplateQuestions={true} // 🔥 ALWAYS: Enable template questions in this mode
                  compactStats={true} // 🔥 COMPACT: Use compact stats
                  showPreviewInHeader={true} // 🔥 NEW: Show preview in section header
                  previewData={previewData} // 🔥 NEW: Pass preview data
                />

                {/* 🔥 NEW: Bot Automation Section */}
                <BotAutomationSection
                  isUpdateAuto={isEdit}
                  taskData={automationData.taskData}
                  dayActions={formLogic.formData.dayActions}
                  setDayActions={(actions: any) => formLogic.updateFormData('dayActions', actions)}
                  ShowInfoAction={ShowInfoAction}
                  setDayDelays={(delays: any) => formLogic.updateFormData('dayDelays', delays)}
                  statusData={automationData.statusData}
                  userListData={automationData.userListData}
                  labelData={automationData.labelData}
                  leadListData={automationData.leadListData}
                  whatsappTemplate={automationData.whatsappTemplates}
                  emailTemplate={automationData.emailTemplates}
                  dayErrors={{}}
                  dayDelays={formLogic.formData.dayDelays}
                  dayMetrics={{}}
                />

              </div>
            )}

            {/* 🎯 RESPONSIVE LAYOUT - When showing single section */}
            {(showOnlyBasicSections || showOnlyContentSections || showOnlyQuestionsSection) && (
              <style>
                {`
                  .${styles.formGrid} {
                    display: flex !important;
                    flex-direction: column !important;
                    width: 100% !important;
                    max-width: none !important;
                    margin: 0 !important;
                  }
                  .${styles.leftColumn}, 
                  .${styles.rightColumn},
                  .${styles.singleColumn} {
                    max-width: none;
                  }
                  .${styles.singleColumn} {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                  }
                `}
              </style>
            )}

            </div>
          </div>

          {/* 🎯 FORM ACTIONS */}
          {!hideHeader && (
            <FormActions {...formActionsProps} />
          )}

        </form>
      </div>
    );
  }, [
    hideHeader, isEdit, onCancel, formLogic, templateLogic, keywordLogic, 
    questionnaireLogic, basicInfoProps, responseTypeProps, formActionsProps, previewData,
    shouldShowBasicSections, shouldShowContentSections, shouldShowQuestionsOnly,
    showOnlyBasicSections, showOnlyContentSections, showOnlyQuestionsSection,
    hideResponseType, enableTemplateQuestions
  ]);

  logger.log('🤖 BotForm: Hook states summary:', {
    formDataValid: !!formLogic.formData,
    questionsCount: questionnaireLogic.questions?.length || 0,
    templatesCount: !!templateLogic.selectedTemplate,
    keywordsCount: keywordLogic.keywords?.length || 0,
    isFormValid: formLogic.isFormValid,
    loading: formLogic.loading,
    saving: formLogic.saving,
    sectionVisibility: {
      basic: shouldShowBasicSections,
      content: shouldShowContentSections,
      questionsOnly: shouldShowQuestionsOnly
    },
    templateQuestionsEnabled: enableTemplateQuestions
  });

  // 🔄 LOADING STATE
  if (formLogic.loading) {
    logger.log('⏳ BotForm: Showing loading state');
    const LoadingContent = (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading bot data...</p>
      </div>
    );

    return hideHeader ? LoadingContent : (
      <div id="main" className="main">
        <Header />
        <Container fluid className="py-4">
          {LoadingContent}
        </Container>
      </div>
    );
  }

  // 🎨 RENDER
  logger.log('🎨 BotForm: Rendering final component, hideHeader:', hideHeader, 'mode:', {
    basicOnly: showOnlyBasicSections,
    contentOnly: showOnlyContentSections,
    questionsOnly: showOnlyQuestionsSection,
    templateQuestionsEnabled: enableTemplateQuestions
  });
  
  const finalContent = (
    <>
      {formContent}

      {/* 🎯 ACTION DRAWER for configuring automation actions (same as automation system) */}
      <DrawerComponent
        openWithHeader={actionBaseDrawer}
        setOpenWithHeader={setActionBaseDrawer}
        drawerTitle={actionDrawerTitle}
        size="xs"
      >
        {actionDrawerTitle === "Send Email" ||
         actionDrawerTitle === "Send Gmail" ||
         actionDrawerTitle === "Send ZeptoMail" ||
         actionDrawerTitle === "Send Whatsapp" ? (
          <div className="template-selection-container">
            <button
              className="btn btn-primary select-template-btn"
              onClick={() => setIsTemplateSelectOpen(true)}
              style={{
                marginBottom: '20px',
                width: '100%'
              }}
            >
              {selectedTemplate?._id ? 'Change Selected Template' : 'Select a Template'}
            </button>

            {selectedTemplate && (
              <div className="selected-template-preview" style={{ marginBottom: '20px' }}>
                <h4>Selected Template:</h4>
                <p><strong>{selectedTemplate.name}</strong></p>
                {selectedTemplate.description && <p>{selectedTemplate.description}</p>}
              </div>
            )}

            <button
              className="btn btn-primary"
              disabled={!selectedTemplate?._id}
              onClick={() => {
                if (selectedTemplate) {
                  const isEmailAction = actionDrawerTitle === "Send Email" ||
                                        actionDrawerTitle === "Send Gmail" ||
                                        actionDrawerTitle === "Send ZeptoMail";
                  onSaveDrawerClose(
                    isEmailAction
                      ? { content: selectedTemplate._id }
                      : { template: selectedTemplate._id }
                  );
                }
              }}
              style={{ width: '100%' }}
            >
              Save Template Selection
            </button>
          </div>
        ) : actionDrawerTitle === "Create task" ? (
          <ActionCreateTask
            addUpdateModalValue={updateTaskActionData}
            onSaveDrawerClose={(taskData) => {
              if (currentEditingAction) {
                const { dayKey, actionIndex } = currentEditingAction;
                const newActions = { ...formLogic.formData.dayActions };
                if (newActions[dayKey] && newActions[dayKey][actionIndex]) {
                  newActions[dayKey][actionIndex] = {
                    ...newActions[dayKey][actionIndex],
                    data: taskData
                  };
                  formLogic.updateFormData('dayActions', newActions);
                }
              }
              setActionBaseDrawer(false);
              setCurrentEditingAction(null);
            }}
          />
        ) : actionDrawerTitle === "Send Webhook" ? (
          <ActionWebhook
            addUpdateModalValue={updateTaskActionData}
            onSaveDrawerClose={(webhookData) => {
              if (currentEditingAction) {
                const { dayKey, actionIndex } = currentEditingAction;
                const newActions = { ...formLogic.formData.dayActions };
                if (newActions[dayKey] && newActions[dayKey][actionIndex]) {
                  newActions[dayKey][actionIndex] = {
                    ...newActions[dayKey][actionIndex],
                    data: webhookData
                  };
                  formLogic.updateFormData('dayActions', newActions);
                }
              }
              setActionBaseDrawer(false);
              setCurrentEditingAction(null);
            }}
          />
        ) : (
          <ActionCommonDrawer
            actionDrawerTitle={actionDrawerTitle}
            selectedAction={selectedAction}
            handlePlusMinusValue={handlePlusMinusValue}
            SubActionSelectOnchange={SubActionSelectOnchange}
            delayValue={delayValue}
            statusData={automationData.statusData}
            labelData={automationData.labelData}
            leadListData={automationData.leadListData}
            userListData={automationData.userListData}
            delayMinMaxMsg={delayMinMaxMsg}
            onSaveDrawerClose={onSaveDrawerClose}
            updateActionDays={updateActionDays}
            isActionError={isActionError}
          />
        )}
      </DrawerComponent>

      {/* 🎯 TEMPLATE SELECTOR (same as automation system) */}
      <UnifiedTemplateSelector
        key={selectedAction}
        isOpen={isTemplateSelectOpen}
        onClose={() => setIsTemplateSelectOpen(false)}
        onTemplateSelect={(template) => {
          handleTemplateSelectFromUnified(template);
        }}
        selectedTemplateId={selectedTemplate?._id}
        templateType={selectedAction === "send_email" ||
          selectedAction === "send_gmail" ||
          selectedAction === "send_zeptomail" ? "email" : "whatsapp"}
        handleDiscard={() => {
          setSelectedTemplate(null);
          setIsTemplateSelectOpen(false);
        }}
      />
    </>
  );

  return hideHeader ? finalContent : (
    <div id="main" className="main">
      <Header />
      <Container fluid className="py-4">
        {finalContent}
      </Container>
    </div>
  );
};

export default BotForm;